import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin } from "lucide-react";
import { scrollTop } from "@/lib/scroll";

const SECTIONS = [
    {
        title: "Overview",
        body: "This Privacy Policy explains how Las Vegas Restroom Locator (\"the app\", \"we\", \"us\") handles information when you use the iOS app or this support website. The short version: we collect as little as possible, your location never leaves your phone, and we never sell your data.",
    },
    {
        title: "Information You Give Us",
        body: "When you submit the support form on this website, we collect your name, email address, chosen category, subject, and message. Each submission is stored as a support ticket (with a ticket ID such as LVRL-XXXXXXXX) and emailed to the app developer so we can respond to you. We use this information only to handle your request and, for data corrections, to improve the app's restroom listings.",
    },
    {
        title: "Location Data",
        body: "The app uses your device's GPS on-device only, to sort nearby restrooms by walking distance. Your location is never uploaded, stored on our servers, shared, or sold. If you deny location permission, the app still works — distances are simply hidden.",
    },
    {
        title: "What We Don't Collect",
        body: "The app requires no account, no sign-in, and no personal profile. We do not use advertising trackers, we do not run analytics that identify you, and we do not build marketing profiles. There is nothing to opt out of because there is nothing collected beyond support requests you explicitly send.",
    },
    {
        title: "Third-Party Services",
        body: "This website is hosted on Emergent's managed infrastructure. Support form emails are delivered through a transactional email provider acting on our behalf. If you download the app, Apple's App Store handles the purchase and download under Apple's own privacy policy. These providers process data only as needed to deliver their service to us.",
    },
    {
        title: "Data Retention & Deletion",
        body: "Support tickets are kept so we can track ongoing issues and improve the app's data. You can ask us to delete any ticket and the personal details attached to it at any time — email lasvegasbathroomlocator@gmail.com from the address you used, include your ticket ID if you have it, and we will remove it within 30 days.",
    },
    {
        title: "Security",
        body: "This site is served over HTTPS, and support submissions are transmitted encrypted. Access to the support inbox and ticket storage is restricted to the app developer.",
    },
    {
        title: "Children's Privacy",
        body: "The app and this site are not directed at children under 13, and we do not knowingly collect personal information from children. If you believe a child has sent us personal information, contact us and we will delete it.",
    },
    {
        title: "Changes to This Policy",
        body: "If this policy changes, the updated version will be posted at this URL with a new \"last updated\" date. Material changes will also be noted in the app's release notes.",
    },
    {
        title: "Contact",
        body: "Questions about this policy or your data? Email lasvegasbathroomlocator@gmail.com or use the support form on the home page — privacy questions get the same 1–2 business day response as everything else.",
    },
];

export default function Privacy() {
    useEffect(() => {
        scrollTop();
    }, []);

    return (
        <div className="min-h-screen bg-[#06070B]" data-testid="privacy-page">
            <header className="border-b border-white/5 bg-[#06070B]/80 backdrop-blur-xl">
                <div className="mx-auto flex max-w-4xl items-center justify-between px-5 py-4 sm:px-8">
                    <a href="/" className="flex items-center gap-2.5" data-testid="privacy-logo">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-neon-cyan/40 bg-neon-cyan/10">
                            <MapPin className="h-5 w-5 text-neon-cyan" strokeWidth={2.2} />
                        </span>
                        <span className="font-display text-xs font-bold tracking-[0.18em] text-white sm:text-sm">
                            LV RESTROOM <span className="text-neon-cyan neon-cyan">LOCATOR</span>
                        </span>
                    </a>
                    <a
                        href="/"
                        data-testid="privacy-back-link"
                        className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-slate-400 transition-colors duration-300 hover:text-neon-cyan"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to site
                    </a>
                </div>
            </header>

            <main className="mx-auto max-w-4xl px-5 py-16 sm:px-8 sm:py-24">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-neon-magenta">
                        Legal — Last updated August 2026
                    </p>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                        PRIVACY <span className="text-neon-cyan neon-cyan">POLICY</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
                        What happens in Vegas stays on your device. Here's exactly what the
                        Las Vegas Restroom Locator app and this website do — and don't do —
                        with your information.
                    </p>
                </motion.div>

                <div className="mt-14 space-y-5">
                    {SECTIONS.map((s, i) => (
                        <motion.section
                            key={s.title}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay: Math.min(i * 0.05, 0.3) }}
                            className="glass-panel rounded-2xl p-6 sm:p-7"
                            data-testid={`privacy-section-${i + 1}`}
                        >
                            <h2 className="mb-3 flex items-baseline gap-3 text-lg font-semibold text-white">
                                <span className="font-mono text-xs text-neon-cyan">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                {s.title}
                            </h2>
                            <p className="text-sm leading-relaxed text-slate-400">{s.body}</p>
                        </motion.section>
                    ))}
                </div>

                <p className="mt-14 border-t border-white/5 pt-8 text-xs text-slate-600" data-testid="privacy-footer-note">
                    © 2026 Las Vegas Restroom Locator. This policy applies to the iOS app and
                    to lasvegasrestroomlocator.com.
                </p>
            </main>
        </div>
    );
}
